import { create } from 'zustand';
import { dimensionApi } from '../services/api';
import type { Dimension, DimensionField } from '../types';
import type { PagedData, ListQueryRequest } from '../types';

interface DimensionState {
  // 状态
  dimensions: Dimension[];
  currentDimension: Dimension | null;
  fields: DimensionField[];
  total: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchDimensions: (params: ListQueryRequest) => Promise<void>;
  fetchDimensionById: (id: string) => Promise<void>;
  createDimension: (data: Partial<Dimension>) => Promise<void>;
  updateDimension: (id: string, data: Partial<Dimension>) => Promise<void>;
  deleteDimension: (id: string) => Promise<void>;
  addField: (dimensionId: string, data: Partial<DimensionField>) => Promise<void>;
  updateField: (dimensionId: string, fieldId: string, data: Partial<DimensionField>) => Promise<void>;
  deleteField: (dimensionId: string, fieldId: string) => Promise<void>;
  setCurrentDimension: (dimension: Dimension | null) => void;
}

export const useDimensionStore = create<DimensionState>((set, get) => ({
  dimensions: [],
  currentDimension: null,
  fields: [],
  total: 0,
  isLoading: false,
  error: null,

  fetchDimensions: async (params: ListQueryRequest) => {
    set({ isLoading: true, error: null });
    try {
      const data = await dimensionApi.getDimensions(params);
      set({ 
        dimensions: data.items || [], 
        total: data.total || 0,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchDimensionById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const dimension = await dimensionApi.getDimensionById(id);
      set({ 
        currentDimension: dimension, 
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  createDimension: async (data: Partial<Dimension>) => {
    set({ isLoading: true, error: null });
    try {
      await dimensionApi.createDimension(data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateDimension: async (id: string, data: Partial<Dimension>) => {
    set({ isLoading: true, error: null });
    try {
      await dimensionApi.updateDimension(id, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deleteDimension: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await dimensionApi.deleteDimension(id);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  addField: async (dimensionId: string, data: Partial<DimensionField>) => {
    set({ isLoading: true, error: null });
    try {
      await dimensionApi.addField(dimensionId, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateField: async (dimensionId: string, fieldId: string, data: Partial<DimensionField>) => {
    set({ isLoading: true, error: null });
    try {
      await dimensionApi.updateField(dimensionId, fieldId, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deleteField: async (dimensionId: string, fieldId: string) => {
    set({ isLoading: true, error: null });
    try {
      await dimensionApi.deleteField(dimensionId, fieldId);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  setCurrentDimension: (dimension: Dimension | null) => {
    set({ currentDimension: dimension });
  },
}));
